/**
 * PiecePanelSection — the leather-piece property panels extracted from
 * PropertiesPanel (#877, epic #828 slice PP-S4). PropertiesPanel OWNS a
 * PiecePanelSection instance and DRIVES it by composition (it is NOT a base
 * class), mirroring the StitchPanelSection / ConstructionLinePanelSection
 * precedents: the section holds the per-piece property BUILDERS (the Part /
 * Cutout / Hole panels, the Hardware tool panel, and the Trace draft / part
 * trace-connections sections), while the `render()` dispatch that decides WHEN
 * to call them stays on PropertiesPanel.
 *
 * Everything the builders read from the owning panel (the DOM append target,
 * the editor context, the panel refresh, the shared promote-button /
 * connection-row builders, the stitching-defaults block owned by the stitch
 * section, and the in-progress Trace draft's line ids) is injected through the
 * `PiecePanelHost` interface. The shared form builders (`textField` /
 * `dimensionField` / `holeDiameterField`) stay single-definition in
 * `./panelFieldBuilders` and are imported here. The Hardware tool's transient
 * state (type / colour / size / side + their change callbacks) is section-local
 * — nothing else on the panel reads it — so it moved in wholesale; the panel
 * keeps thin `setHardware*Handler` delegators for the app.ts wiring. Behaviour
 * is identical to the in-panel original — this is a pure move + host rewiring.
 */
import type { EditorContext } from "../tools/EditorContext";
import { markPaidFeature, markPaidSection } from "./paidPreview";
import type {
  Cutout,
  HardwareColor,
  HardwareSide,
  HardwareType,
  Hole,
  Piece,
  Project,
} from "../model/Project";
import { pieceStitchLines, projectConstructionLines } from "../model/Project";
import {
  HARDWARE_COLOR_LABELS,
  HARDWARE_COLORS,
  HARDWARE_DEFAULT_SIZE_MM,
  HARDWARE_SIZES_MM,
  HARDWARE_TYPE_LABELS,
} from "../hardware/HardwareCatalog";
import { HARDWARE_TYPE_ICONS, hasSide } from "../hardware/HardwareSymbols";
import type { ConnectionRowSpec } from "../model/connections";
import {
  partTraceRowSpecs,
  subTraceRowSpecs,
  TRACE_CHIP_ICON_SVG,
} from "../model/connections";
import { partSourceLineIds } from "../model/constructionOutline";
import { EYE_SVG, EYE_OFF_SVG } from "./layersRowKit";
import {
  promotePieceToConstruction,
  promoteCutoutToConstruction,
} from "../model/promoteToConstruction";
import {
  clearPieceLayout,
  hasPieceLayoutOffset,
  transformPieceByAffine,
} from "../model/pieceTransform";
import { isFeatureEnabled } from "../featureFlags";
import paper from "paper";
import {
  fieldNote,
  holeDiameterField,
  numberField,
  panelSection,
  textField,
  dimensionField,
  RESMOOTH_DISCARDS_EDITS_NOTE,
} from "./panelFieldBuilders";
import {
  DEFAULT_LEATHER_SMOOTHNESS,
  hasFreehandSource,
} from "../model/leatherSmoothing";
import {
  smoothnessFromPercent,
  smoothnessPercent,
} from "../model/freehandSmoothing";

export interface PiecePanelHost {
  /** The panel's root append target — every piece panel's final append. */
  getRoot(): HTMLElement;
  /** The shared editor context (history / commit / scene / selection). */
  getCtx(): EditorContext;
  /** Rebuild the properties panel DOM for the current selection. */
  refresh(): void;
  /** The "promote to construction" button builder (shared with the stitch
   *  section). */
  promoteButton(what: string, onClick: () => void): HTMLButtonElement;
  /** One Connections-section row (shared with the stitch / construction
   *  panels). */
  buildConnectionRow(spec: ConnectionRowSpec): HTMLLIElement;
  /** The in-progress Trace draft's referenced construction-line ids (set from
   *  app.ts via the panel's `setTraceDraft`). */
  getTraceDraftLineIds(): string[];
  /** The stitching-defaults editor lives on the stitch section; the part panel
   *  embeds it, so the panel exposes it here. */
  buildStitchingDefaultsBlock(
    heading: string,
    target:
      | "project"
      | { pieceId: string }
      | { pieceId: string; lineIds: string[] },
  ): HTMLElement;
}

export class PiecePanelSection {
  constructor(private host: PiecePanelHost) {}

  /** The editor context is owned by PropertiesPanel and reached through the
   *  host; the piece builders read history / commit / scene / selection off it. */
  private get ctx(): EditorContext {
    return this.host.getCtx();
  }
  /** The panel's DOM append target, reached through the host. */
  private get root(): HTMLElement {
    return this.host.getRoot();
  }
  /** The in-progress Trace draft's line ids, reached through the host so the
   *  moved body stays byte-identical. */
  private get traceDraftLineIds(): string[] {
    return this.host.getTraceDraftLineIds();
  }
  /** Thin delegators so the moved bodies keep calling `this.refresh()` /
   *  `this.promoteButton(...)` / … unchanged; the real implementations stay
   *  single-definition on the panel (or, for the stitching-defaults block, on
   *  the stitch section). */
  private refresh(): void {
    this.host.refresh();
  }
  private promoteButton(what: string, onClick: () => void): HTMLButtonElement {
    return this.host.promoteButton(what, onClick);
  }
  private buildConnectionRow(spec: ConnectionRowSpec): HTMLLIElement {
    return this.host.buildConnectionRow(spec);
  }
  private buildStitchingDefaultsBlock(
    heading: string,
    target:
      | "project"
      | { pieceId: string }
      | { pieceId: string; lineIds: string[] },
  ): HTMLElement {
    return this.host.buildStitchingDefaultsBlock(heading, target);
  }

  // — Hardware tool state (section-local; app.ts wires it through the panel's
  //   `setHardware*Handler` delegators). —
  private hardwareType: HardwareType = "rivet";
  private hardwareColor: HardwareColor = "silver";
  private hardwareSizeMm = 9;
  private hardwareSide: HardwareSide = "female";
  private onHardwareTypeChange?: (type: HardwareType) => void;
  private onHardwareColorChange?: (color: HardwareColor) => void;
  private onHardwareSizeChange?: (mm: number) => void;
  private onHardwareSideChange?: (side: HardwareSide) => void;

  setHardwareTypeHandler(fn: (type: HardwareType) => void) {
    this.onHardwareTypeChange = fn;
  }
  setHardwareColorHandler(fn: (color: HardwareColor) => void) {
    this.onHardwareColorChange = fn;
  }
  setHardwareSizeHandler(fn: (mm: number) => void) {
    this.onHardwareSizeChange = fn;
  }
  setHardwareSideHandler(fn: (side: HardwareSide) => void) {
    this.onHardwareSideChange = fn;
  }

  /** Builds the Hardware tool's properties section: type gallery → side
   *  toggle (only for snap / magnetic-snap) → colour swatches → size
   *  preset chips (filtered to the picked type's common sizes) → help
   *  text. All four selectors push their changes back through the panel
   *  handlers wired in `app.ts`, so the live ghost preview on the
   *  HardwareTool follows along. */
  public appendHardwarePanel() {
    // — Type gallery —
    const typeWrap = document.createElement("div");
    typeWrap.className = "field";
    const typeLbl = document.createElement("label");
    typeLbl.textContent = "Hardware";
    typeWrap.appendChild(typeLbl);
    const typeGrid = document.createElement("div");
    typeGrid.className = "shape-gallery";
    const typeOrder: HardwareType[] = [
      "rivet",
      "snap",
      "magnetic-snap",
      "eyelet",
      "concho-round",
      "concho-star",
      "concho-floral",
    ];
    for (const key of typeOrder) {
      const tile = document.createElement("button");
      tile.type = "button";
      tile.className = "shape-tile";
      if (key === this.hardwareType) tile.classList.add("selected");
      const label = HARDWARE_TYPE_LABELS[key];
      tile.title = label;
      tile.setAttribute("aria-label", label);
      tile.innerHTML = HARDWARE_TYPE_ICONS[key];
      tile.addEventListener("click", () => {
        this.hardwareType = key;
        // Snap the size to the type's default — the previous size from a
        // different type often falls outside this type's preset chips.
        this.hardwareSizeMm = HARDWARE_DEFAULT_SIZE_MM[key];
        this.onHardwareTypeChange?.(key);
        this.onHardwareSizeChange?.(this.hardwareSizeMm);
        this.refresh();
      });
      typeGrid.appendChild(tile);
    }
    typeWrap.appendChild(typeGrid);
    this.root.appendChild(typeWrap);

    // — Side toggle (only for snaps / magnetic snaps) —
    if (hasSide(this.hardwareType)) {
      const sideWrap = document.createElement("div");
      sideWrap.className = "field";
      const sideLbl = document.createElement("label");
      sideLbl.textContent = "Side";
      sideWrap.appendChild(sideLbl);
      const sideRow = document.createElement("div");
      sideRow.className = "preset-row";
      for (const opt of [
        { value: "female" as const, label: "Top (female)" },
        { value: "male" as const, label: "Bottom (male)" },
      ]) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "preset-chip";
        btn.textContent = opt.label;
        if (this.hardwareSide === opt.value) btn.classList.add("active");
        btn.addEventListener("click", () => {
          this.hardwareSide = opt.value;
          this.onHardwareSideChange?.(opt.value);
          this.refresh();
        });
        sideRow.appendChild(btn);
      }
      sideWrap.appendChild(sideRow);
      this.root.appendChild(sideWrap);
    }

    // — Colour swatches —
    const colorWrap = document.createElement("div");
    colorWrap.className = "field";
    const colorLbl = document.createElement("label");
    colorLbl.textContent = "Colour";
    colorWrap.appendChild(colorLbl);
    const swatchRow = document.createElement("div");
    swatchRow.className = "hardware-color-row";
    const colorOrder: HardwareColor[] = [
      "silver",
      "brass",
      "antiqued-brass",
      "black",
      "gun-metal",
    ];
    for (const c of colorOrder) {
      const sw = document.createElement("button");
      sw.type = "button";
      sw.className = "hardware-color-swatch";
      if (c === this.hardwareColor) sw.classList.add("selected");
      sw.style.background = HARDWARE_COLORS[c];
      sw.title = HARDWARE_COLOR_LABELS[c];
      sw.setAttribute("aria-label", HARDWARE_COLOR_LABELS[c]);
      sw.addEventListener("click", () => {
        this.hardwareColor = c;
        this.onHardwareColorChange?.(c);
        this.refresh();
      });
      swatchRow.appendChild(sw);
    }
    colorWrap.appendChild(swatchRow);
    this.root.appendChild(colorWrap);

    // — Size preset chips (filtered to this type's sizes) —
    const sizeWrap = document.createElement("div");
    sizeWrap.className = "field";
    const sizeLbl = document.createElement("label");
    sizeLbl.textContent = "Size";
    sizeWrap.appendChild(sizeLbl);
    const sizeRow = document.createElement("div");
    sizeRow.className = "preset-row";
    const sizes = HARDWARE_SIZES_MM[this.hardwareType];
    const matches = (a: number, b: number) => Math.abs(a - b) < 0.01;
    for (const s of sizes) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "preset-chip";
      chip.textContent = s.label;
      if (matches(this.hardwareSizeMm, s.mm)) chip.classList.add("active");
      chip.addEventListener("click", () => {
        this.hardwareSizeMm = s.mm;
        this.onHardwareSizeChange?.(s.mm);
        this.refresh();
      });
      sizeRow.appendChild(chip);
    }
    sizeWrap.appendChild(sizeRow);
    this.root.appendChild(sizeWrap);

    // — Helper —
    const help = document.createElement("div");
    help.style.color = "var(--text-dim)";
    help.style.fontSize = "11px";
    help.style.marginTop = "4px";
    help.textContent =
      "Click a hole to seat hardware. Click bare leather to drill a hole sized for the post and seat the hardware in one step.";
    this.root.appendChild(help);
  }

  /** Renders Name + Width / Height inputs for the selected part. Renaming
   *  commits a single history entry. Editing width or height scales the whole
   *  part (outline + holes + cutouts + decorations) around its bounding-box
   *  top-left so the position stays anchored. */
  public appendPartPanel(piece: Piece) {
    const scope = this.ctx.scene.getPaperScope();
    const path = new scope.CompoundPath({
      pathData: piece.shapePathData,
      insert: false,
    });
    const b = path.bounds;
    const w = b.width;
    const h = b.height;

    const wrap = document.createElement("div");
    const heading = document.createElement("h3");
    heading.textContent = "Part";
    wrap.appendChild(heading);

    wrap.appendChild(
      textField("Name", piece.name, (newName) => {
        const id = piece.id;
        this.ctx.commit("Rename part", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) =>
            pc.id === id ? { ...pc, name: newName } : pc,
          ),
        }));
      }),
    );
    wrap.appendChild(
      dimensionField("Width (mm)", w, (newW) => {
        if (newW <= 0 || Math.abs(newW - w) < 1e-4) return;
        this.resizePart(piece.id, newW, h);
      }),
    );
    wrap.appendChild(
      dimensionField("Height (mm)", h, (newH) => {
        if (newH <= 0 || Math.abs(newH - h) < 1e-4) return;
        this.resizePart(piece.id, w, newH);
      }),
    );
    // Reset cut-layout offset (#677): a linked part dragged off its
    // construction position in Spread view carries a `layout` offset — offer a
    // one-click snap back to where it was created. Gated on the same flag as
    // the rest of the construction-connections / cut-layout feature.
    if (
      isFeatureEnabled("construction-connections") &&
      hasPieceLayoutOffset(piece)
    ) {
      const resetBtn = document.createElement("button");
      resetBtn.type = "button";
      resetBtn.className = "btn";
      resetBtn.style.width = "100%";
      resetBtn.style.marginTop = "8px";
      resetBtn.textContent = "Reset cut-layout offset";
      resetBtn.title =
        "Snap this part back to its construction (assembled) position";
      markPaidFeature(resetBtn, "construction-connections");
      resetBtn.addEventListener("click", () => {
        const id = piece.id;
        this.ctx.commit("Reset cut-layout offset", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((p) =>
            p.id === id ? clearPieceLayout(p) : p,
          ),
        }));
      });
      wrap.appendChild(resetBtn);
    }
    // Promote to construction-backed (#740): a plain drawn part can be linked to
    // a fresh construction line matching its outline, so it becomes editable /
    // offsettable / mirror-able like a traced part. Paid feature — same gate as
    // the rest of construction connections; hidden once the part is backed.
    if (
      isFeatureEnabled("construction-connections") &&
      !piece.constructionOutline
    ) {
      const id = piece.id;
      wrap.appendChild(
        this.promoteButton("this part", () =>
          this.ctx.commit(
            "Create construction line",
            (proj) => promotePieceToConstruction(proj, id, scope) ?? proj,
          ),
        ),
      );
    }

    // Smoothness (#1016). Only a freehand-traced part that still carries its
    // raw pointer samples offers this — after a destructive point edit the
    // outline is ordinary geometry and there is nothing left to re-derive from,
    // exactly as construction's slider behaves (#171).
    if (hasFreehandSource(piece)) {
      const id = piece.id;
      wrap.appendChild(
        numberField(
          "Smoothness (%)",
          smoothnessPercent(piece.smoothness ?? DEFAULT_LEATHER_SMOOTHNESS),
          0,
          100,
          1,
          (pct) => {
            const v = smoothnessFromPercent(pct);
            this.ctx.commit("Adjust smoothness", (proj) => ({
              ...proj,
              pieces: proj.pieces.map((pc) =>
                // Applying smoothness makes the outline a derived view of the
                // trace again, so the derive pass takes over from here (#248).
                pc.id === id
                  ? { ...pc, smoothness: v, freehandEdited: undefined }
                  : pc,
              ),
            }));
            // The caution below is about edits that no longer exist. A commit
            // doesn't rebuild the panel while a field has focus — which this
            // one does, having just been typed in — so ask for it.
            if (piece.freehandEdited) this.refresh();
          },
        ),
      );
      if (piece.freehandEdited) {
        wrap.appendChild(fieldNote(RESMOOTH_DISCARDS_EDITS_NOTE));
      }
    }

    this.root.appendChild(wrap);

    // Trace connections: the construction lines this part's outline follows.
    this.appendPartTraceConnectionsSection(piece);

    // Inline access to this part's stitching defaults — saves a trip back
    // to the Layers panel when the user is already inspecting the part.
    if (pieceStitchLines(piece).length > 0 || piece.stitchingDefaults) {
      this.root.appendChild(
        this.buildStitchingDefaultsBlock("Stitching defaults", {
          pieceId: piece.id,
        }),
      );
    }
  }

  /** While the Trace tool is active, list the construction lines the in-
   *  progress outline follows — the same "Traces …" rows a finished part shows,
   *  updated as each segment is added. */
  public appendTraceDraftSection() {
    const allLines = projectConstructionLines(this.ctx.history.state);
    const section = panelSection("Tracing");

    if (this.traceDraftLineIds.length === 0) {
      const hint = document.createElement("p");
      hint.className = "constraints-empty-hint";
      hint.textContent =
        "Click along construction lines to trace a part outline.";
      section.appendChild(hint);
      this.root.appendChild(section);
      return;
    }

    const list = document.createElement("ul");
    list.className = "constraints-list";
    for (const lineId of this.traceDraftLineIds) {
      const line = allLines.find((l) => l.id === lineId);
      const name = line
        ? (line.name ?? `Construction ${allLines.indexOf(line) + 1}`)
        : "construction line";
      const li = document.createElement("li");
      li.className = "constraints-row trace-chip";
      li.dataset.connectionKind = "trace";
      const iconHost = document.createElement("span");
      iconHost.className = "constraints-row-icon";
      iconHost.innerHTML = TRACE_CHIP_ICON_SVG;
      li.appendChild(iconHost);
      const label = document.createElement("span");
      label.className = "constraints-row-label";
      label.textContent = `Traces ${name}`;
      li.appendChild(label);
      if (line) {
        li.addEventListener("mouseenter", () =>
          this.ctx.scene.setConstructionConstraintHover({
            refs: [{ kind: "line", constructionId: lineId }],
          }),
        );
        li.addEventListener("mouseleave", () =>
          this.ctx.scene.setConstructionConstraintHover(null),
        );
      }
      list.appendChild(li);
    }
    section.appendChild(list);
    this.root.appendChild(section);
  }

  /** Connections section on a traced part: one row per construction line its
   *  outline follows (the mirror of the "Traced by …" rows the construction-
   *  line panel shows). Detaching bakes the part (drops the link, keeps the
   *  geometry). Shares each row's connectionKey with the line-side chip/row so
   *  hovering cross-highlights them. */
  private appendPartTraceConnectionsSection(piece: Piece) {
    // The row specs (incl. the #221 click-to-select target) are owned by the
    // trace connection so the part-side rows can't drift from the part chip —
    // both come from the same module. `partTraceRowSpecs` returns [] when the
    // `construction-connections` flag is off or the piece isn't traced.
    const rows = partTraceRowSpecs(this.ctx.history.state, piece);
    // The section also carries the show/hide-sources button (#965), which
    // applies to the part's cut-outs and seams as well — so a hand-drawn part
    // with traced cut-outs has a section even though its OUTLINE follows
    // nothing and there are no rows to list.
    const toggle = isFeatureEnabled("construction-connections")
      ? this.buildSourceVisibilityToggle(piece)
      : null;
    if (rows.length === 0 && !toggle) return;

    const section = panelSection("Connections");
    markPaidSection(section, "construction-connections");
    if (rows.length > 0) {
      const list = document.createElement("ul");
      list.className = "constraints-list";
      for (const spec of rows) list.appendChild(this.buildConnectionRow(spec));
      section.appendChild(list);
    }
    if (toggle) section.appendChild(toggle);
    this.root.appendChild(section);
  }

  /**
   * One-click show / hide of every construction line behind this part (#965).
   *
   * The capability already existed — each line has its own eye in the Parts
   * list, and the Connections rows above jump to them — but a part traced from
   * six lines meant six trips, and the reason you want them gone (the
   * scaffolding is in the way of looking at the leather) applies to all of them
   * at once. This is the part-local shortcut, next to the rows that name the
   * lines it acts on.
   *
   * It covers everything on the part that traces — the outline, the cut-outs
   * and the seams — not just the outline's own sources: a hand-drawn part with
   * traced cut-outs has scaffolding too, and leaving that behind would make the
   * button look broken.
   *
   * MIXED counts as shown, so the first press always hides. Half-hidden is the
   * state you get to by using the per-line eyes, and the one press that helps
   * there is the one that clears the canvas.
   */
  private buildSourceVisibilityToggle(piece: Piece): HTMLElement | null {
    const lineIds = partSourceLineIds(piece);
    if (lineIds.length === 0) return null;
    const lines = projectConstructionLines(this.ctx.history.state);
    const mine = lines.filter((l) => lineIds.includes(l.id));
    if (mine.length === 0) return null;
    const anyVisible = mine.some((l) => l.visible !== false);
    const ids = new Set(mine.map((l) => l.id));

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn source-visibility-btn";
    btn.style.width = "100%";
    btn.style.marginTop = "8px";
    const what =
      mine.length === 1 ? "source line" : `${mine.length} source lines`;
    // The EYE reports the lines' current STATE; the label says what a click
    // does. That split is the Parts-list convention (a hidden row wears a
    // slashed eye, and clicking it un-hides), and the same two glyphs come from
    // the same place — one visibility idea, one visual vocabulary.
    const eye = document.createElement("span");
    eye.className = "source-visibility-eye";
    eye.setAttribute("aria-hidden", "true");
    eye.innerHTML = anyVisible ? EYE_SVG : EYE_OFF_SVG;
    btn.appendChild(eye);
    const label = document.createElement("span");
    label.textContent = anyVisible ? `Hide ${what}` : `Show ${what}`;
    btn.appendChild(label);
    btn.title = anyVisible
      ? "Hide the construction lines this part is traced from"
      : "Show the construction lines this part is traced from";
    btn.dataset.sourceVisibility = anyVisible ? "shown" : "hidden";
    btn.addEventListener("click", () => {
      this.ctx.commit(
        anyVisible ? "Hide source lines" : "Show source lines",
        (proj) => ({
          ...proj,
          constructionLines: projectConstructionLines(proj).map((l) =>
            ids.has(l.id) ? { ...l, visible: !anyVisible } : l,
          ),
        }),
      );
      // The button's own label flips with the state it reports, and a commit
      // doesn't rebuild the panel on its own.
      this.refresh();
    });
    return btn;
  }

  private resizePart(pieceId: string, newW: number, newH: number) {
    const scope = this.ctx.scene.getPaperScope();
    // A resize keeps the part where it is — no auto overlap resolution (it
    // nudged deliberately-placed parts and was slow at scale). Overlap is only
    // auto-resolved on Create.
    this.ctx.commit("Resize part", (proj) => {
      return resizePiece(proj, pieceId, newW, newH, scope);
    });
  }

  public appendCutoutPanel(pieceId: string, cut: Cutout, idx: number) {
    const scope = this.ctx.scene.getPaperScope();
    const path = new scope.CompoundPath({
      pathData: cut.pathData,
      insert: false,
    });
    const b = path.bounds;
    const w = b.width;
    const h = b.height;

    const wrap = document.createElement("div");
    const heading = document.createElement("h3");
    heading.textContent = "Cutout";
    wrap.appendChild(heading);

    const fallbackName = `Cutout ${idx + 1}`;
    wrap.appendChild(
      textField("Name", cut.name ?? fallbackName, (newName) => {
        this.ctx.commit("Rename cutout", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) =>
            pc.id === pieceId
              ? {
                  ...pc,
                  cutouts: pc.cutouts.map((c) =>
                    c.id === cut.id ? { ...c, name: newName } : c,
                  ),
                }
              : pc,
          ),
        }));
      }),
    );
    wrap.appendChild(
      dimensionField("Width (mm)", w, (newW) => {
        if (newW <= 0 || Math.abs(newW - w) < 1e-4) return;
        this.resizeCutout(pieceId, cut.id, newW, h);
      }),
    );
    wrap.appendChild(
      dimensionField("Height (mm)", h, (newH) => {
        if (newH <= 0 || Math.abs(newH - h) < 1e-4) return;
        this.resizeCutout(pieceId, cut.id, w, newH);
      }),
    );
    // Smoothness (#1016) — freehand-traced cutouts only, same rule as parts.
    if (hasFreehandSource(cut)) {
      wrap.appendChild(
        numberField(
          "Smoothness (%)",
          smoothnessPercent(cut.smoothness ?? DEFAULT_LEATHER_SMOOTHNESS),
          0,
          100,
          1,
          (pct) => {
            const v = smoothnessFromPercent(pct);
            this.ctx.commit("Adjust smoothness", (proj) => ({
              ...proj,
              pieces: proj.pieces.map((pc) =>
                pc.id === pieceId
                  ? {
                      ...pc,
                      cutouts: pc.cutouts.map((c) =>
                        c.id === cut.id
                          ? { ...c, smoothness: v, freehandEdited: undefined }
                          : c,
                      ),
                    }
                  : pc,
              ),
            }));
            if (cut.freehandEdited) this.refresh();
          },
        ),
      );
      if (cut.freehandEdited) {
        wrap.appendChild(fieldNote(RESMOOTH_DISCARDS_EDITS_NOTE));
      }
    }
    // A traced cutout lists the lines it follows (#1202), the same rows a
    // traced part shows; × detaches it and keeps its shape.
    const traceRows = subTraceRowSpecs(this.ctx.history.state, {
      kind: "cutout",
      pieceId,
      id: cut.id,
    });
    if (traceRows.length > 0) {
      const section = panelSection("Connections");
      markPaidSection(section, "construction-connections");
      const list = document.createElement("ul");
      list.className = "constraints-list";
      for (const spec of traceRows)
        list.appendChild(this.buildConnectionRow(spec));
      section.appendChild(list);
      wrap.appendChild(section);
    }
    // Promote to construction-backed (#740).
    if (
      isFeatureEnabled("construction-connections") &&
      !cut.constructionOutline
    ) {
      wrap.appendChild(
        this.promoteButton("this cutout", () =>
          this.ctx.commit(
            "Create construction line",
            (proj) =>
              promoteCutoutToConstruction(proj, pieceId, cut.id, scope) ?? proj,
          ),
        ),
      );
    }
    this.root.appendChild(wrap);
  }

  private resizeCutout(
    pieceId: string,
    cutoutId: string,
    newW: number,
    newH: number,
  ) {
    const scope = this.ctx.scene.getPaperScope();
    this.ctx.commit("Resize cutout", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              cutouts: pc.cutouts.map((c) => {
                if (c.id !== cutoutId) return c;
                const cp = new scope.CompoundPath({
                  pathData: c.pathData,
                  insert: false,
                });
                const b = cp.bounds;
                if (b.width === 0 || b.height === 0) return c;
                const sx = newW / b.width;
                const sy = newH / b.height;
                cp.scale(sx, sy, new scope.Point(b.x, b.y));
                return { ...c, pathData: cp.pathData };
              }),
            },
      ),
    }));
  }

  public appendHolePanel(pieceId: string, hole: Hole) {
    const wrap = document.createElement("div");
    const heading = document.createElement("h3");
    heading.textContent = "Hole";
    wrap.appendChild(heading);

    wrap.appendChild(
      holeDiameterField(hole.radius * 2, (newDiameter) => {
        const newR = newDiameter / 2;
        if (newR <= 0 || Math.abs(newR - hole.radius) < 1e-4) return;
        this.ctx.commit("Resize hole", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) =>
            pc.id === pieceId
              ? {
                  ...pc,
                  holes: pc.holes.map((h) =>
                    h.id === hole.id ? { ...h, radius: newR } : h,
                  ),
                }
              : pc,
          ),
        }));
      }),
    );
    this.root.appendChild(wrap);
  }
}

/** Returns a new Project with the named piece scaled to newW × newH. Anchors
 *  the part's bbox top-left so position stays put; decorations, holes, cutouts,
 *  and stitch lines scale proportionally. Delegates to the shared
 *  `transformPieceByAffine` (#971) so the numeric W/H field and the on-canvas
 *  resize drag can't drift. */
function resizePiece(
  proj: Project,
  pieceId: string,
  newW: number,
  newH: number,
  scope: paper.PaperScope,
): Project {
  const piece = proj.pieces.find((p) => p.id === pieceId);
  if (!piece) return proj;

  const path = new scope.CompoundPath({
    pathData: piece.shapePathData,
    insert: false,
  });
  const b = path.bounds;
  path.remove();
  if (b.width === 0 || b.height === 0) return proj;

  // Top-left-anchored box-to-box: origin fixed, no flip.
  const orig = { x: b.x, y: b.y, width: b.width, height: b.height };
  const next = { x: b.x, y: b.y, width: newW, height: newH };
  const updated = transformPieceByAffine(
    piece,
    orig,
    next,
    { x: false, y: false },
    scope,
  );
  return {
    ...proj,
    pieces: proj.pieces.map((p) => (p.id === pieceId ? updated : p)),
  };
}
